import { useUserInfo } from "@hooks/useUserInfo/useUserInfo";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { Spinner } from "@jmc/solid-design-system/src/components/atoms/Spinner/Spinner";
import { default as stripSlash } from "@jmc/utils/utils/strip-slash";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { log } from "loglevel";
import React, { useEffect, useState } from "react";

import { AuthProviders } from "../../api/authProviders";
import { useUrlPrefix } from "../../contexts/SiteContext";
import { AccessLevel } from "../../types/AccessLevel";
import { JWMLoginDisclaimer } from "../JWMLoginDisclaimer/JWMLoginDisclaimer";

interface PropTypes {
    gatedUrl?: string;
    children: (content: object) => JSX.Element;
    publicContent?: CMSBlock[];
    content?: CMSBlock[];
    level?: AccessLevel;
    declaration?: boolean;
}

/**
 * Returns content as an object, for the freeFormPages of level 1
 *
 * @param content the public content to be wrapped
 */
export const wrapContentForFreeFormPage = (content: CMSBlock[]): { content: CMSBlock[] } => ({
    content,
});

export const AccessControl = (props: PropTypes): JSX.Element => {
    const { gatedUrl = "", children, publicContent = [], level, content = [], declaration = false } = props;
    const locale = useLocale();
    const userInfo = useUserInfo();
    const isBrowser = typeof document !== "undefined";

    // State
    const [gatedContent, setGatedContent] = useState(null);

    const prefix = useUrlPrefix();

    const hasPublicContent = (publicContent?: CMSBlock[]): boolean => {
        if (
            (publicContent && Array.isArray(publicContent) && publicContent.length) ||
            (typeof publicContent === "object" && Object.keys(publicContent).length)
        ) {
            return true;
        }
        return false;
    };

    const getRedirectUrl = (): string => {
        // Drop the query string and the locale from the URL
        const redirectUrl = isBrowser ? window.location.pathname : "";
        const finalUrl = redirectUrl.replace(`/${locale}`, "");

        return finalUrl;
    };

    const fetchGatedContent = (): void => {
        let canAccessPage = false;

        if (level === AccessLevel.Private && userInfo.hasProvider()) {
            canAccessPage = true;
        }

        if (
            level === AccessLevel.AccessCode &&
            userInfo.hasProvider(AuthProviders.accessCode) &&
            userInfo.isValidAccessCodeForPage(getRedirectUrl())
        ) {
            canAccessPage = true;
        }

        if (gatedUrl && canAccessPage) {
            // This GET call will only respond successfully if we provide the right access
            // token as a cookie.
            fetch(gatedUrl, {
                method: "GET",
                credentials: "include",
            })
                .then((response) => {
                    if (!response.ok) {
                        if (response.status === 401) {
                            return null;
                        }
                        throw new Error(
                            `Something went wrong fetching private page data: ${response.status} ${response.statusText}`,
                        );
                    }
                    return response.json();
                })
                .then((data) => {
                    return setGatedContent(data);
                })
                .catch((error) => log.error(error));
        }
    };

    // Side Effects
    useEffect(fetchGatedContent, []);
    useEffect(fetchGatedContent, [userInfo.authData]);

    if (level === AccessLevel.Public || declaration) {
        return children(content ? wrapContentForFreeFormPage(content) : publicContent);
    }

    if (level === AccessLevel.AccessCode) {
        if (Object.entries(userInfo.authData).length !== 0 && !userInfo.isAuthInProgress()) {
            let doRedirect = false;
            const redirectUrl = getRedirectUrl();

            if (!userInfo.hasProvider(AuthProviders.accessCode)) {
                doRedirect = true;
            } else {
                doRedirect = !userInfo.isValidAccessCodeForPage(getRedirectUrl());
            }

            if (isBrowser && doRedirect) {
                const domain = stripSlash.stripTrailing(process.env.GATSBY_APP_DOMAIN);
                const finalRedirectUrl = redirectUrl === "" ? "/" : redirectUrl;
                const queryString = isBrowser ? window.location.search : "";
                const redirectUrlParams = new URLSearchParams(queryString);
                const originalParams = redirectUrlParams?.toString() !== "" ? `&${redirectUrlParams.toString()}` : "";

                window.location.href = `${domain}/${locale}/access-code?prefix=${prefix}&url=${finalRedirectUrl}${originalParams}`;
            }
        }
    }

    if (!gatedContent && !hasPublicContent(publicContent)) {
        if (level === AccessLevel.AccessCode) {
            return null;
        }

        if (userInfo.isAuthPending() || userInfo.isAuthInProgress()) {
            return (
                <div style={{ padding: "40vh" }}>
                    <Spinner size="xl" />
                </div>
            );
        }
        return <JWMLoginDisclaimer />;
    }

    if (!gatedContent && hasPublicContent(publicContent)) {
        return children(publicContent);
    }
    return children(gatedContent);
};

export default AccessControl;
